import { useState, useEffect, memo, useRef } from 'react'
import { useSmartNavigation } from '../hooks/useSmartNavigation'
import { reportsApi, checkinsApi } from '../services/api'
import { offlineStorage } from '../services/offlineStorage'
import toast from 'react-hot-toast'
import {
  DocumentChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  MapPinIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import ReportModal from '../components/IncidentReportModal'

interface Report {
  summary: string
  id: string
  type: 'incident' | 'hourly'
  title: string
  location: string | {
    id: string
    name: string
    address: string
  } | null
  createdAt: string
  status: string
  isDraft?: boolean
}

interface CheckInStatus {
  id: string
  location: {
    name: string
    address: string
  }
  checkInTime: string
  isWithinGeofence: boolean
}

interface ReportsProps {
  user: any
}

// Memoized header component to prevent re-rendering
const ReportsHeader = memo(() => (
  <div className="space-y-2 min-h-[80px]">
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <div className="p-3 rounded-xl bg-gradient-orange shadow-glow-sm animate-pulse-orange">
          <DocumentChartBarIcon className="h-8 w-8 text-white" />
        </div>
        <div>
          <h1 className="text-display-sm font-display text-gradient">Reports</h1>
          <p className="text-body text-gray-600 dark:text-tech-400 font-mono">
            Track your incident and hourly reports
          </p>
        </div>
      </div>
    </div>
  </div>
))

ReportsHeader.displayName = 'ReportsHeader'

// Memoized tab navigation component
const TabNavigation = memo(({
  activeTab,
  onTabChange,
  isTransitioning
}: {
  activeTab: 'incidents' | 'hourly'
  onTabChange: (tab: 'incidents' | 'hourly') => void
  isTransitioning: boolean
}) => {
  const getTabStyle = (tab: string) => {
    const isActive = activeTab === tab
    return `px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ease-in-out ${isActive
      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-glow transform scale-105'
      : 'bg-gray-200 dark:bg-tech-700 text-gray-700 dark:text-tech-300 hover:bg-gray-300 dark:hover:bg-tech-600 hover:scale-105'
      }`
  }

  return (
    <div className="flex space-x-2 h-12 items-center">
      <button
        onClick={() => onTabChange('incidents')}
        className={getTabStyle('incidents')}
        disabled={isTransitioning}
      >
        <ExclamationTriangleIcon className="h-4 w-4 inline mr-2" />
        Incidents
      </button>
      <button
        onClick={() => onTabChange('hourly')}
        className={getTabStyle('hourly')}
        disabled={isTransitioning}
      >
        <ClockIcon className="h-4 w-4 inline mr-2" />
        Hourly Reports
      </button>
    </div>
  )
})

TabNavigation.displayName = 'TabNavigation'

// Memoized stats grid component
const StatsGrid = memo(({
  reportStats,
  isTransitioning,
  activeTab
}: {
  reportStats: {
    todayIncidents: number
    todayHourly: number
    weekIncidents: number
    weekHourly: number
  }
  isTransitioning: boolean
  activeTab: 'incidents' | 'hourly'
}) => {
  console.log('🔍 StatsGrid - Report stats:', reportStats)
  const incidentStatCards = [
    {
      name: 'Today Incidents',
      value: reportStats.todayIncidents,
      icon: ExclamationTriangleIcon,
      gradient: 'from-error to-red-600',
      description: 'Incidents today'
    },
    {
      name: 'Week Incidents',
      value: reportStats.weekIncidents,
      icon: ExclamationTriangleIcon,
      gradient: 'from-warning to-accent-amber',
      description: 'Incidents this week'
    }
  ]

  const hourlyStatCards = [
    {
      name: 'Today Hourly',
      value: reportStats.todayHourly,
      icon: ClockIcon,
      gradient: 'from-secondary-500 to-secondary-600',
      description: 'Hourly reports today'
    },
    {
      name: 'Week Hourly',
      value: reportStats.weekHourly,
      icon: DocumentChartBarIcon,
      gradient: 'from-success to-accent-emerald',
      description: 'Hourly reports this week'
    }
  ]

  const statCards = activeTab === 'incidents' ? incidentStatCards : hourlyStatCards

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-h-[80px]">
      {statCards.map((stat, index) => (
        <div key={stat.name} className={`stat-card animate-slide-up delay-${index * 100} transition-all duration-300 ${isTransitioning ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
          }`}>
          <div className="flex items-start justify-between mb-2">
            <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.gradient} shadow-glow-sm transition-all duration-300 ${isTransitioning ? 'animate-pulse' : ''
              }`}>
              <stat.icon className="h-6 w-6 text-white stat-icon" />
            </div>
          </div>
          <div className="space-y-1">
            <div className="stat-value transition-all duration-300">{stat.value}</div>
            <div className="text-body-sm text-gray-600 dark:text-tech-400 font-medium">{stat.name}</div>
            <div className="text-caption text-gray-700 dark:text-tech-500">{stat.description}</div>
          </div>
        </div>
      ))}
    </div>
  )
})

StatsGrid.displayName = 'StatsGrid'

// Clean loading spinner component
const LoadingSpinner = memo(() => (
  <div className="flex items-center justify-center py-12">
    <div className="flex flex-col items-center space-y-4">
      <div className="relative">
        <div className="w-8 h-8 border-2 border-gray-300 dark:border-tech-600 rounded-full animate-spin border-t-primary-500"></div>
      </div>
      <p className="text-sm text-gray-600 dark:text-tech-400">Loading reports...</p>
    </div>
  </div>
))

LoadingSpinner.displayName = 'LoadingSpinner'

export default function Reports({ user }: ReportsProps) {
  const { navigateToHourlyReport, navigateToIncidentReport, navigate } = useSmartNavigation()
  const [reports, setReports] = useState<Report[]>([])
  const [currentCheckIn, setCurrentCheckIn] = useState<CheckInStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [showSpinner, setShowSpinner] = useState(false)
  const [activeTab, setActiveTab] = useState<'incidents' | 'hourly'>('incidents')
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [reportStats, setReportStats] = useState({
    todayIncidents: 0,
    todayHourly: 0,
    weekIncidents: 0,
    weekHourly: 0
  })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null)
  // Prevent older async loads from overwriting newer results
  const loadIdRef = useRef(0)

  useEffect(() => {
    loadReports()
    loadCurrentCheckIn()
  }, [activeTab])

  const loadReports = async () => {
    console.log('🔍 Loading reports...')
    const loadId = ++loadIdRef.current
    let spinnerTimer: NodeJS.Timeout | null = null

    try {
      setLoading(true)
      setShowSpinner(false)

      // Set a timer to show spinner after 2 seconds
      spinnerTimer = setTimeout(() => {
        if (loading) {
          setShowSpinner(true)
        }
      }, 2000)

      // Load reports (both published and drafts)
      // These will be used to compute stats without relying on state
      let incidentsPublishedForStats: any[] = []
      let hourlyPublishedForStats: any[] = []

      if (activeTab === 'incidents') {
        let publishedResponse, draftsResponse

        try {
          // Try to load from API first
          [publishedResponse, draftsResponse] = await Promise.all([
            reportsApi.getIncidentReports(),
            reportsApi.getDraftIncidentReports()
          ])
        } catch (error) {
          console.log('🔍 API failed, loading drafts from offline storage')
          // Fallback to offline storage
          draftsResponse = offlineStorage.getOfflineReportDrafts().filter(d => d.type === 'incident')
          publishedResponse = []
        }

        console.log('📊 Incident reports response:', publishedResponse)
        console.log('📊 Incident drafts response:', draftsResponse)

        // Ensure we have arrays before calling .map()
        const publishedData = Array.isArray(publishedResponse) ? publishedResponse :
          Array.isArray(publishedResponse?.data) ? publishedResponse.data : []

        const draftsData = Array.isArray(draftsResponse) ? draftsResponse :
          Array.isArray(draftsResponse?.data) ? draftsResponse.data : []

        // Always merge offline drafts (unsynced) so they show even when API succeeds
        const offlineIncidentDrafts = offlineStorage.getOfflineReportDrafts()
          .filter(d => d.type === 'incident' && !d.synced)
          .map(d => ({
            id: String(d.reportId || d.localId),
            type: 'incident',
            title: d.data?.title || 'Incident Draft (Offline)',
            summary: d.data?.summary || d.data?.title || 'Incident Draft (Offline)',
            location: d.data?.location || d.data?.locationName || null,
            createdAt: (d.timestamp as any) || new Date().toISOString(),
            status: 'draft',
            isDraft: true
          }))

        const publishedReports = publishedData.map((report: any) => ({
          ...report,
          isDraft: false,
          status: 'published'
        }))

        const draftReports = draftsData.map((report: any) => ({
          ...report,
          isDraft: true,
          status: 'draft'
        }))

        // Combine drafts with de-duplication by id (prefer API drafts)
        const draftMap = new Map<string, any>()
        offlineIncidentDrafts.forEach((d: any) => draftMap.set(String(d.id), d))
        draftReports.forEach((d: any) => draftMap.set(String(d.id), d))

        // Combine and sort by creation date (newest first)
        const allReports = [...publishedReports, ...Array.from(draftMap.values())]
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

        // Update list for UI (guard against stale loads)
        if (loadId === loadIdRef.current) {
          setReports(allReports)
        }

        // For stats
        incidentsPublishedForStats = publishedData
      } else {
        console.log('🔍 Loading hourly reports...')

        let publishedResponse, draftsResponse

        try {
          // Try to load from API first
          [publishedResponse, draftsResponse] = await Promise.all([
            reportsApi.getHourlyReports(),
            reportsApi.getDraftHourlyReports()
          ])
        } catch (error) {
          console.log('🔍 API failed, loading drafts from offline storage')
          // Fallback to offline storage
          draftsResponse = offlineStorage.getOfflineReportDrafts().filter(d => d.type === 'hourly')
          publishedResponse = []
        }

        console.log('📊 Hourly reports response:', publishedResponse)
        console.log('📊 Draft hourly reports response:', draftsResponse)

        // Ensure we have arrays before calling .map()
        const publishedData = Array.isArray(publishedResponse) ? publishedResponse :
          Array.isArray(publishedResponse?.data) ? publishedResponse.data : []

        const draftsData = Array.isArray(draftsResponse) ? draftsResponse :
          Array.isArray(draftsResponse?.data) ? draftsResponse.data : []

        // Always merge offline drafts (unsynced)
        const offlineHourlyDrafts = offlineStorage.getOfflineReportDrafts()
          .filter(d => d.type === 'hourly' && !d.synced)
          .map(d => ({
            id: String(d.reportId || d.localId),
            type: 'hourly',
            title: d.data?.title || 'Hourly Draft (Offline)',
            summary: d.data?.summary || d.data?.title || 'Hourly Draft (Offline)',
            location: d.data?.location || d.data?.locationName || null,
            createdAt: (d.timestamp as any) || new Date().toISOString(),
            status: 'draft',
            isDraft: true
          }))

        const publishedReports = publishedData.map((report: any) => ({
          ...report,
          isDraft: false,
          status: 'published'
        }))

        const draftReports = draftsData.map((report: any) => ({
          ...report,
          isDraft: true,
          status: 'draft'
        }))

        // Combine drafts with de-duplication by id (prefer API drafts), then sort
        const draftMap = new Map<string, any>()
        offlineHourlyDrafts.forEach((d: any) => draftMap.set(String(d.id), d))
        draftReports.forEach((d: any) => draftMap.set(String(d.id), d))

        const allReports = [...publishedReports, ...Array.from(draftMap.values())]
          .sort((a, b) => {
            if (a.isDraft && !b.isDraft) return -1
            if (!a.isDraft && b.isDraft) return 1
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          })

        console.log('📊 Combined reports:', allReports)

        // Update list for UI (guard against stale loads)
        if (loadId === loadIdRef.current) {
          setReports(allReports)
        }

        // For stats
        hourlyPublishedForStats = publishedData
      }

      // Load report statistics
      try {
        const today = new Date();

        // Force `today` to be the start of the current day
        today.setHours(0, 0, 0, 0);

        // Find the start of the current week (Sunday as day 0)
        const weekAgo = new Date(today); // clone
        weekAgo.setDate(today.getDate() - today.getDay());
        weekAgo.setHours(0, 0, 0, 0);

        // Use already fetched lists for the active tab, and fetch the other one
        if (activeTab === 'incidents') {
          const hourlyRes = await reportsApi.getHourlyReports()
          hourlyPublishedForStats = Array.isArray(hourlyRes) ? hourlyRes : Array.isArray(hourlyRes?.data) ? hourlyRes.data : []
        } else {
          const incidentsRes = await reportsApi.getIncidentReports()
          incidentsPublishedForStats = Array.isArray(incidentsRes) ? incidentsRes : Array.isArray(incidentsRes?.data) ? incidentsRes.data : []
        }

        const incidentsData = incidentsPublishedForStats
        const hourlyData = hourlyPublishedForStats

        const todayStr = today.toDateString()
        const todayIncidents = incidentsData.filter((r: any) => new Date(r.createdAt).toDateString() === todayStr).length
        const todayHourly = hourlyData.filter((r: any) => new Date(r.createdAt).toDateString() === todayStr).length

        const weekIncidents = incidentsData.filter((r: any) => new Date(r.createdAt) >= weekAgo).length
        const weekHourly = hourlyData.filter((r: any) => new Date(r.createdAt) >= weekAgo).length

        const stats = {
          todayIncidents,
          todayHourly,
          weekIncidents,
          weekHourly
        }

        if (loadId === loadIdRef.current) {
          setReportStats(stats)
        }
      } catch (error) {
        console.error('❌ Error loading report stats:', error)
      }
    } catch (error) {
      console.error('❌ Error loading reports:', error)
      toast.error('Failed to load reports')
    } finally {
      if (spinnerTimer) {
        clearTimeout(spinnerTimer)
      }
      setLoading(false)
      setShowSpinner(false)
    }
  }

  const loadCurrentCheckIn = async () => {
    try {
      const response = await checkinsApi.getCurrent()
      if (response) {
        setCurrentCheckIn(response)
      }
    } catch (error) {
      console.error('❌ Failed to load current check-in:', error)
    }
  }

  const handleEditDraft = async (report: Report) => {
    if (report.isDraft) {
      if (activeTab === 'incidents') {
        await navigateToIncidentReport()
      } else {
        await navigateToHourlyReport()
      }
    }
  }

  const handleDeleteDraft = async (report: Report) => {
    if (!report.isDraft) return

    try {
      console.log('🔍 Deleting draft:', report)
      // Check if this is an offline draft (id starts with 'local-')
      const isOfflineDraft = report.id.startsWith('local-')
      
      if (isOfflineDraft) {
        // Delete from offline storage
        offlineStorage.clearReportDraft(report.id)
        toast.success('Offline draft deleted successfully')
      } else {
        // Delete from server
        if (activeTab === 'incidents') {
          await reportsApi.deleteIncidentReportDraft(report.id)
        } else {
          await reportsApi.deleteHourlyReportDraft(report.id)
        }
        toast.success('Draft deleted successfully')
      }

      // Reload reports to update the list
      loadReports()
    } catch (error) {
      console.error('Error deleting draft:', error)
      toast.error('Failed to delete draft')
    }
  }

  const handleViewReport = (report: Report) => {
    if (report.isDraft) {
      handleEditDraft(report)
    } else {
      // For published reports, open the modal
      setSelectedReportId(report.id)
      setIsModalOpen(true)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedReportId(null)
  }

  const handleTabChange = (tab: 'incidents' | 'hourly') => {
    if (tab === activeTab) return

    setIsTransitioning(true)
    setActiveTab(tab)

    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false)
    }, 300)
  }

  const getLocationName = (location: any): string => {
    if (!location) return 'Unknown Location'
    if (typeof location === 'string') return location
    if (typeof location === 'object' && location.name) return location.name
    return 'Unknown Location'
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Invalid date'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'submitted':
      case 'published':
        return 'text-success bg-success/20 border-success/30'
      case 'draft':
      case 'pending':
        return 'text-warning bg-warning/20 border-warning/30'
      case 'cancelled':
        return 'text-error bg-error/20 border-error/30'
      default:
        return 'text-tech-400 bg-tech-700/20 border-tech-700/30'
    }
  }

  console.log('🔍 Reports:', reports)

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-6 pb-24 space-y-2 animate-fade-in">
      {/* Static header - won't re-render */}
      <ReportsHeader />

      {/* Tab navigation - memoized */}
      <TabNavigation
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isTransitioning={isTransitioning}
      />

      {/* Stats Grid - memoized */}
      <StatsGrid reportStats={reportStats} isTransitioning={isTransitioning} activeTab={activeTab} />

      {/* Small spacer for better separation */}
      <div className="h-5"></div>

      {/* Current Check-In Status - Compact */}
      {currentCheckIn && (
        <div className="premium-card p-4 transition-all duration-300 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-success to-accent-emerald">
                <CheckCircleIcon className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-body font-medium text-gray-900 dark:text-tech-50">Currently Checked In</h3>
                <p className="text-body-sm text-gray-600 dark:text-tech-400">{currentCheckIn.location.name}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => navigateToHourlyReport()}
                className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-lg hover:shadow-glow-blue transition-all duration-300 hover:-translate-y-0.5 text-sm"
              >
                <ClockIcon className="w-4 h-4" />
                <span className="font-medium">Hourly</span>
              </button>
              <button
                onClick={() => navigateToIncidentReport()}
                className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-warning to-accent-amber text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5 text-sm"
              >
                <ExclamationTriangleIcon className="w-4 h-4" />
                <span className="font-medium">Incident</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reports List - Dynamic height container */}
      <div className="premium-card p-4 min-h-[200px] transition-all duration-300">
        <div className="flex items-center space-x-3 mb-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-tech-700 to-tech-600">
            <ChartBarIcon className="h-5 w-5 text-tech-300" />
          </div>
          <h2 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display transition-all duration-300">
            {activeTab === 'incidents' ? 'Incident Reports' : 'Hourly Reports'}
          </h2>
        </div>

        <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}>
          {loading && showSpinner ? (
            <LoadingSpinner />
          ) : reports.length === 0 ? (
            <div className="text-center py-8">
              <div className="p-3 rounded-full bg-gray-100 dark:bg-tech-800 inline-flex mb-3">
                {activeTab === 'incidents' ? (
                  <ExclamationTriangleIcon className="w-10 h-10 text-gray-400 dark:text-tech-500" />
                ) : (
                  <ClockIcon className="w-10 h-10 text-gray-400 dark:text-tech-500" />
                )}
              </div>
              <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display mb-2">
                No {activeTab === 'incidents' ? 'Incident' : 'Hourly'} Reports
              </h3>
              <p className="text-body text-gray-600 dark:text-tech-400 mb-4">
                {activeTab === 'incidents'
                  ? 'You haven\'t submitted any incident reports yet.'
                  : 'You haven\'t submitted any hourly reports yet.'}
              </p>
              {currentCheckIn && (
                <button
                  onClick={() => activeTab === 'incidents' ? navigateToIncidentReport() : navigateToHourlyReport()}
                  className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5 mx-auto"
                >
                  <PlusIcon className="w-5 h-5" />
                  <span className="text-body-sm font-medium">
                    Create {activeTab === 'incidents' ? 'Incident' : 'Hourly'} Report
                  </span>
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {(() => {
                return null
              })()}
              {reports.map((report, index) => {
                return (
                  <div
                    key={report.id}
                    className={`p-4 bg-gray-50 dark:bg-tech-800/30 rounded-lg hover:bg-gray-100 dark:hover:bg-tech-800/50 transition-all duration-300 animate-slide-up delay-${index * 50} ${report.isDraft ? 'cursor-pointer' : ''
                      }`}
                    onClick={() => report.isDraft ? handleEditDraft(report) : undefined}
                  >
                    {/* Header with icon, title and status - Mobile optimized */}
                    <div className="flex items-start space-x-3 mb-3">
                      <div className="p-2 rounded-lg bg-gradient-to-br from-secondary-500 to-secondary-600 flex-shrink-0">
                        {activeTab === 'incidents' ? (
                          <ExclamationTriangleIcon className="w-5 h-5 text-white" />
                        ) : (
                          <ClockIcon className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-body font-medium text-gray-900 dark:text-tech-50 truncate">
                          {report.summary || report.title || 'Untitled Report'}
                        </h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(report.status || 'draft')}`}>
                            {report.status || 'draft'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Location and date info */}
                    <div className="flex flex-wrap items-center gap-2 text-body-sm text-gray-600 dark:text-tech-400 mb-3">
                      <span className="flex items-center">
                        <MapPinIcon className="w-4 h-4 mr-1" />
                        {getLocationName(report.location)}
                      </span>
                      <span>•</span>
                      <span className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-1" />
                        {formatDate(report.createdAt)}
                      </span>
                    </div>

                    {/* Action buttons - Top aligned for mobile */}
                    <div className="flex items-center justify-end space-x-2">
                      {report.isDraft ? (
                        // Draft reports show edit and delete buttons
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEditDraft(report)
                            }}
                            className="flex items-center space-x-2 px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors rounded-lg text-sm font-medium"
                            title="Edit draft"
                          >
                            <PencilIcon className="w-4 h-4" />
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteDraft(report)
                            }}
                            className="flex items-center space-x-2 px-3 py-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors rounded-lg text-sm font-medium"
                            title="Delete draft"
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span>Delete</span>
                          </button>
                        </>
                      ) : (
                        // Published reports show view button
                        <button
                          onClick={() => handleViewReport(report)}
                          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-tech-700 text-gray-600 dark:text-tech-300 hover:bg-gray-200 dark:hover:bg-tech-600 transition-colors rounded-lg text-sm font-medium"
                          title="View report"
                        >
                          <EyeIcon className="w-4 h-4" />
                          <span>View</span>
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions - Dynamic height */}
      <div className="min-h-[40px] mb-8">
        {!currentCheckIn && (
          <div className="premium-card p-6 transition-all duration-300 animate-fade-in">
            <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display mb-4">Quick Actions</h3>
            <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-full bg-warning/20">
                  <ExclamationTriangleIcon className="w-5 h-5 text-warning" />
                </div>
                <div>
                  <p className="text-body font-medium text-warning">Not Checked In</p>
                  <p className="text-body-sm text-gray-600 dark:text-tech-400">You need to check in at a location to create reports.</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/status')}
                className="mt-4 flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-success to-accent-emerald text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5"
              >
                <CheckCircleIcon className="w-4 h-4" />
                <span className="text-body-sm font-medium">Check In Now</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        reportId={selectedReportId}
        reportType={activeTab === 'incidents' ? 'incident' : 'hourly'}
      />
    </div>
  )
}